// Validation Dashboard - Startup Validation Ladder Analysis
// Redesigned with improved UX, collapsible panels, and better screen usage

import { useState } from 'react'
import {
  Target,
  Play,
  RefreshCw,
  CheckCircle2,
  Circle,
  FileText,
  Loader2,
  ChevronDown,
  ChevronUp,
  Quote,
  Lightbulb,
  ClipboardCheck,
  TrendingUp,
} from 'lucide-react'
import {
  useValidationStages,
  useLatestCompletedAnalysis,
  useStartValidationAnalysis,
  usePollingValidationAnalysis,
} from '@/hooks/useValidationAnalysis'
import type { ValidationStageKey } from '@ispot/shared'
import type { ValidationAnalysisResponse, ValidationStageResponse } from '@/lib/api/client'

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

// Use the API response type for stage scores to avoid type mismatches
type ApiStageScore = ValidationAnalysisResponse['stageScores'][number]
type ApiSignalScore = ApiStageScore['signals'][number]
type StageSignal = ValidationStageResponse['signals'][number]
type SignalUnion = ApiSignalScore | StageSignal

interface StageInfo {
  key: ValidationStageKey
  name: string
  description: string
  icon: React.ReactNode
}

const STAGES: StageInfo[] = [
  {
    key: 'pe',
    name: 'Problem existence',
    description: 'Is there a real problem worth solving?',
    icon: <Target className="w-4 h-4" />,
  },
  {
    key: 'pu',
    name: 'Problem urgency',
    description: 'Are customers actively seeking solutions?',
    icon: <TrendingUp className="w-4 h-4" />,
  },
  {
    key: 'psf',
    name: 'Problem-solution fit',
    description: 'Does your solution address the problem?',
    icon: <Lightbulb className="w-4 h-4" />,
  },
  {
    key: 'bmf',
    name: 'Business model fit',
    description: 'Can you build a sustainable business?',
    icon: <ClipboardCheck className="w-4 h-4" />,
  },
  {
    key: 'pmf',
    name: 'Product-market fit',
    description: 'Do customers love your product?',
    icon: <CheckCircle2 className="w-4 h-4" />,
  },
]

// ============================================================================
// SPIDER DIAGRAM - Larger with 10 levels
// ============================================================================

function SpiderDiagram({
  scores,
  selectedStage,
  onSelectStage,
}: {
  scores: Record<ValidationStageKey, number>
  selectedStage: ValidationStageKey | null
  onSelectStage: (key: ValidationStageKey) => void
}) {
  // Responsive size with generous padding for labels
  const size = 520
  const center = size / 2
  const radius = size * 0.32 // Slightly smaller radius to give more room for labels
  const levels = 10

  const getPoint = (stageIndex: number, level: number) => {
    const angle = (stageIndex * 72 - 90) * (Math.PI / 180)
    const r = (level / levels) * radius
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle),
    }
  }

  const dataPoints = STAGES.map((stage, i) => {
    const score = scores[stage.key] ?? 0
    const level = (score / 100) * levels
    return getPoint(i, level)
  })

  const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'

  // Get label position with position-specific offsets to prevent cutoff
  const getLabelPosition = (stageIndex: number) => {
    const angle = (stageIndex * 72 - 90) * (Math.PI / 180)
    // Use different radius for each position to optimize space
    const labelOffsets = [60, 70, 70, 70, 70] // Top, top-right, bottom-right, bottom-left, top-left
    const labelRadius = radius + labelOffsets[stageIndex]
    return {
      x: center + labelRadius * Math.cos(angle),
      y: center + labelRadius * Math.sin(angle),
    }
  }

  return (
    <div className="relative flex items-center justify-center w-full h-full min-h-[400px]">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="w-full h-full max-w-[520px] max-h-[520px]"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Grid levels - show every 20% for cleaner look */}
        {[2, 4, 6, 8, 10].map((level) => {
          const points = STAGES.map((_, i) => getPoint(i, level))
          const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z'
          return (
            <g key={level}>
              <path
                d={path}
                className="fill-none stroke-warm-200"
                strokeWidth={level === 10 ? 1.5 : 1}
                strokeDasharray={level < 10 ? '3 3' : '0'}
              />
              {/* Percentage labels on the top axis */}
              {level <= 10 && (
                <text
                  x={center + 8}
                  y={center - (level / levels) * radius - 4}
                  className="fill-warm-400 text-[10px]"
                  textAnchor="start"
                >
                  {level * 10}%
                </text>
              )}
            </g>
          )
        })}

        {/* Axis lines */}
        {STAGES.map((_, i) => {
          const outerPoint = getPoint(i, levels)
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={outerPoint.x}
              y2={outerPoint.y}
              className="stroke-warm-200"
              strokeWidth={1}
            />
          )
        })}

        {/* Data polygon with gradient fill */}
        <defs>
          <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#5c8a6b" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#3d7a8c" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <path
          d={dataPath}
          fill="url(#scoreGradient)"
          className="stroke-business"
          strokeWidth={2.5}
        />

        {/* Data points */}
        {dataPoints.map((point, i) => {
          const isSelected = selectedStage === STAGES[i].key
          const score = scores[STAGES[i].key] ?? 0
          return (
            <g key={i}>
              <circle
                cx={point.x}
                cy={point.y}
                r={isSelected ? 10 : 7}
                className={`cursor-pointer transition-all duration-200 ${
                  isSelected
                    ? 'fill-business stroke-white'
                    : 'fill-white stroke-business hover:fill-business/20'
                }`}
                strokeWidth={2.5}
                onClick={() => onSelectStage(STAGES[i].key)}
              />
              {/* Score label on point */}
              {isSelected && (
                <text
                  x={point.x}
                  y={point.y + 4}
                  textAnchor="middle"
                  className="fill-white text-[10px] font-bold pointer-events-none"
                >
                  {score}
                </text>
              )}
            </g>
          )
        })}

        {/* Stage name labels with position-specific adjustments */}
        {STAGES.map((stage, i) => {
          const pos = getLabelPosition(i)
          const isSelected = selectedStage === stage.key
          const score = scores[stage.key] ?? 0

          // Position-specific text alignment
          // 0 = top, 1 = top-right, 2 = bottom-right, 3 = bottom-left, 4 = top-left
          let textAnchor: 'start' | 'middle' | 'end' = 'middle'
          let dx = 0
          let dy = 0

          if (i === 0) {
            // Top - center aligned, pull down slightly
            textAnchor = 'middle'
            dy = -5
          } else if (i === 1) {
            // Top-right - left aligned
            textAnchor = 'start'
            dx = 5
          } else if (i === 2) {
            // Bottom-right - left aligned, push down
            textAnchor = 'start'
            dx = 5
            dy = 5
          } else if (i === 3) {
            // Bottom-left - right aligned, push down
            textAnchor = 'end'
            dx = -5
            dy = 5
          } else if (i === 4) {
            // Top-left - right aligned
            textAnchor = 'end'
            dx = -5
          }

          return (
            <g
              key={stage.key}
              className="cursor-pointer"
              onClick={() => onSelectStage(stage.key)}
            >
              <text
                x={pos.x + dx}
                y={pos.y + dy - 6}
                textAnchor={textAnchor}
                className={`font-display text-[13px] font-semibold transition-colors ${
                  isSelected ? 'fill-business' : 'fill-warm-700 hover:fill-business'
                }`}
              >
                {stage.name}
              </text>
              <text
                x={pos.x + dx}
                y={pos.y + dy + 10}
                textAnchor={textAnchor}
                className={`text-[12px] font-medium ${
                  isSelected ? 'fill-business' : 'fill-warm-500'
                }`}
              >
                {score}%
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// ============================================================================
// COLLAPSIBLE SECTION COMPONENT
// ============================================================================

function CollapsibleSection({
  title,
  icon,
  defaultOpen = true,
  badge,
  children,
}: {
  title: string
  icon: React.ReactNode
  defaultOpen?: boolean
  badge?: React.ReactNode
  children: React.ReactNode
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-warm-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-warm-50 hover:bg-warm-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-warm-600">{icon}</span>
          <span className="font-display text-sm font-semibold text-warm-800">{title}</span>
          {badge}
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-warm-500" />
        ) : (
          <ChevronDown className="w-4 h-4 text-warm-500" />
        )}
      </button>
      {isOpen && <div className="p-4 bg-white">{children}</div>}
    </div>
  )
}

// ============================================================================
// STAGE NAVIGATION ITEM
// ============================================================================

function StageNavItem({
  stage,
  score,
  isSelected,
  stageNumber,
  onClick,
}: {
  stage: StageInfo
  score?: ApiStageScore
  isSelected: boolean
  stageNumber: number
  onClick: () => void
}) {
  const confidence = score?.confidenceScore ?? 0
  const status = score?.status ?? 'not_started'

  const getStatusColor = () => {
    if (status === 'validated') return 'bg-market text-white'
    if (status === 'in_progress') return 'bg-solution text-white'
    return 'bg-warm-200 text-warm-600'
  }

  const getProgressColor = () => {
    if (confidence >= 70) return 'bg-market'
    if (confidence >= 40) return 'bg-solution'
    return 'bg-problem'
  }

  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        isSelected
          ? 'bg-white border-business shadow-lg ring-2 ring-business/10'
          : 'bg-white border-warm-100 hover:border-warm-300 hover:shadow-md'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${getStatusColor()}`}>
          {stageNumber}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h4 className="font-display text-sm font-semibold text-warm-900 truncate">
              {stage.name}
            </h4>
            <span className="font-display text-lg font-bold text-warm-900 ml-2">
              {confidence}%
            </span>
          </div>
          <p className="text-xs text-warm-500 mb-2 line-clamp-1">
            {stage.description}
          </p>
          <div className="h-1.5 bg-warm-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${getProgressColor()}`}
              style={{ width: `${confidence}%` }}
            />
          </div>
        </div>
      </div>
    </button>
  )
}

// ============================================================================
// STAGE DETAIL PANEL - With Collapsible Sections
// ============================================================================

function StageDetailPanel({
  stageKey,
  stageScore,
}: {
  stageKey: ValidationStageKey
  stageScore?: ApiStageScore
}) {
  const { data: stages } = useValidationStages()
  const stage = stages?.find((s) => s.key === stageKey)
  const stageInfo = STAGES.find((s) => s.key === stageKey)

  if (!stage) {
    return <div className="animate-pulse bg-warm-100 rounded-xl h-96" />
  }

  const validatedCount = stageScore?.signals?.filter((s: ApiStageScore['signals'][number]) => s.isValidated).length ?? 0
  const totalSignals = stageScore?.signals?.length ?? stage.signals.length

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-xl border border-warm-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-business/10 flex items-center justify-center">
                {stageInfo?.icon}
              </div>
              <div>
                <h2 className="font-display text-xl font-bold text-warm-900">
                  {stage.name}
                </h2>
                <p className="text-sm text-warm-600">{stage.question}</p>
              </div>
            </div>
          </div>
          <div className="text-right ml-4">
            <div className="font-display text-4xl font-bold text-warm-900">
              {stageScore?.confidenceScore ?? 0}%
            </div>
            <div className="text-xs text-warm-500 tracking-wide">Confidence</div>
          </div>
        </div>

        {/* Score bar */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-warm-500 mb-1">
            <span>Validation progress</span>
            <span>{validatedCount} of {totalSignals} signals validated</span>
          </div>
          <div className="h-3 bg-warm-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${stageScore?.confidenceScore ?? 0}%`,
                background: `linear-gradient(90deg, #5c8a6b 0%, #3d7a8c 100%)`,
              }}
            />
          </div>
        </div>
      </div>

      {/* Collapsible Sections - default collapsed for cleaner view */}
      <CollapsibleSection
        title="Validation signals"
        icon={<ClipboardCheck className="w-4 h-4" />}
        defaultOpen={false}
        badge={
          <span className="px-2 py-0.5 bg-warm-200 text-warm-700 text-xs rounded-full">
            {validatedCount}/{totalSignals}
          </span>
        }
      >
        <div className="space-y-3">
          {(stageScore?.signals ?? stage.signals).map((signal: SignalUnion, idx: number) => {
            const isValidated = 'isValidated' in signal ? signal.isValidated : false
            const signalName = 'signalName' in signal ? signal.signalName : signal.name
            const evidenceSummary = 'evidenceSummary' in signal ? signal.evidenceSummary : null
            const signalKey = 'signalKey' in signal ? signal.signalKey : signal.key

            return (
              <div
                key={signalKey || idx}
                className={`flex items-start gap-3 p-3 rounded-lg border ${
                  isValidated
                    ? 'bg-market/5 border-market/20'
                    : 'bg-warm-50 border-warm-100'
                }`}
              >
                {isValidated ? (
                  <CheckCircle2 className="w-5 h-5 text-market mt-0.5 flex-shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-warm-400 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-warm-800">
                    {signalName}
                  </div>
                  {evidenceSummary && (
                    <p className="text-xs text-warm-600 mt-1">{evidenceSummary}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </CollapsibleSection>

      {stageScore?.evidenceQuotes && stageScore.evidenceQuotes.length > 0 && (
        <CollapsibleSection
          title="Supporting evidence"
          icon={<Quote className="w-4 h-4" />}
          defaultOpen={false}
          badge={
            <span className="px-2 py-0.5 bg-business/10 text-business text-xs rounded-full">
              {stageScore.evidenceQuotes.length} quotes
            </span>
          }
        >
          <div className="space-y-4">
            {stageScore.evidenceQuotes.map((quote: ApiStageScore['evidenceQuotes'][number], i: number) => (
              <blockquote
                key={i}
                className="border-l-3 border-business pl-4 py-2 bg-business/5 rounded-r-lg"
              >
                <p className="text-sm text-warm-700 italic leading-relaxed">
                  "{quote.quote}"
                </p>
                <cite className="text-xs text-warm-500 not-italic mt-2 block">
                  — {quote.sourceTitle}
                </cite>
              </blockquote>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {stageScore?.recommendations && stageScore.recommendations.length > 0 && (
        <CollapsibleSection
          title="Recommendations"
          icon={<Lightbulb className="w-4 h-4" />}
          defaultOpen={false}
          badge={
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
              {stageScore.recommendations.length} actions
            </span>
          }
        >
          <div className="space-y-3">
            {stageScore.recommendations.map((rec: ApiStageScore['recommendations'][number], i: number) => (
              <div
                key={i}
                className={`p-4 rounded-lg border-l-4 ${
                  rec.priority === 'high'
                    ? 'bg-red-50 border-l-red-500'
                    : rec.priority === 'medium'
                    ? 'bg-amber-50 border-l-amber-500'
                    : 'bg-warm-50 border-l-warm-300'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded ${
                      rec.priority === 'high'
                        ? 'bg-red-100 text-red-700'
                        : rec.priority === 'medium'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-warm-200 text-warm-700'
                    }`}
                  >
                    {rec.priority === 'high' ? 'High priority' : rec.priority === 'medium' ? 'Medium priority' : 'Low priority'}
                  </span>
                </div>
                <h5 className="text-sm font-semibold text-warm-900 mb-1">{rec.title}</h5>
                <p className="text-xs text-warm-600 leading-relaxed">{rec.description}</p>
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}
    </div>
  )
}

// ============================================================================
// EMPTY STATE
// ============================================================================

function EmptyState({ onRunAnalysis }: { onRunAnalysis: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="w-20 h-20 rounded-2xl bg-business/10 flex items-center justify-center mb-6">
        <Target className="w-10 h-10 text-business" />
      </div>
      <h3 className="font-display text-2xl font-bold text-warm-900 mb-3">
        Start your validation journey
      </h3>
      <p className="text-warm-600 max-w-lg mb-8 leading-relaxed">
        Let's analyze your product intelligence to see where you stand on the validation ladder. We'll look at everything from problem existence to product-market fit.
      </p>
      <button
        onClick={onRunAnalysis}
        className="flex items-center gap-3 px-8 py-4 bg-business text-white rounded-xl font-semibold hover:bg-business-dark transition-colors shadow-lg shadow-business/20"
      >
        <Play className="w-5 h-5" />
        Run validation analysis
      </button>
    </div>
  )
}

// ============================================================================
// RUNNING STATE
// ============================================================================

function RunningState({
  progress,
}: {
  progress: { stepsCompleted: number; totalSteps: number; currentStepName: string | null }
}) {
  const stepNames = [
    'Gathering your product intelligence...',
    'Evaluating each validation stage...',
    'Creating recommendations for you...',
  ]
  const currentStep = stepNames[progress.stepsCompleted] ?? 'Processing...'

  return (
    <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
      <div className="w-20 h-20 rounded-2xl bg-business/10 flex items-center justify-center mb-6">
        <Loader2 className="w-10 h-10 text-business animate-spin" />
      </div>
      <h3 className="font-display text-2xl font-bold text-warm-900 mb-3">
        Analyzing your product
      </h3>
      <p className="text-warm-600 max-w-lg mb-8">{currentStep}</p>
      <div className="w-80 h-2 bg-warm-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-business rounded-full transition-all duration-500"
          style={{ width: `${((progress.stepsCompleted + 1) / progress.totalSteps) * 100}%` }}
        />
      </div>
      <p className="text-xs text-warm-500 mt-3">
        Step {progress.stepsCompleted + 1} of {progress.totalSteps}
      </p>
    </div>
  )
}

// ============================================================================
// MAIN DASHBOARD
// ============================================================================

interface ValidationDashboardProps {
  onViewReport?: (artifactId: string) => void
}

export function ValidationDashboard({ onViewReport }: ValidationDashboardProps) {
  const [selectedStage, setSelectedStage] = useState<ValidationStageKey>('pe')
  const [runningAnalysisId, setRunningAnalysisId] = useState<string | null>(null)

  const { data: latestAnalysis, isLoading } = useLatestCompletedAnalysis()
  const startAnalysis = useStartValidationAnalysis()
  const { isRunning, isComplete, progress } = usePollingValidationAnalysis(runningAnalysisId)

  const scores = STAGES.reduce(
    (acc, stage) => {
      const stageScore = latestAnalysis?.stageScores?.find((s) => s.stageKey === stage.key)
      acc[stage.key] = stageScore?.confidenceScore ?? 0
      return acc
    },
    {} as Record<ValidationStageKey, number>
  )

  const handleRunAnalysis = async () => {
    try {
      const analysis = await startAnalysis.mutateAsync()
      setRunningAnalysisId(analysis.id)
    } catch (error) {
      console.error('Failed to start analysis:', error)
    }
  }

  if (isComplete && runningAnalysisId) {
    setRunningAnalysisId(null)
  }

  const selectedStageScore = latestAnalysis?.stageScores?.find(
    (s) => s.stageKey === selectedStage
  )

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-warm-200 rounded-lg w-64" />
          <div className="h-96 bg-warm-100 rounded-xl" />
        </div>
      </div>
    )
  }

  if (isRunning) {
    return <RunningState progress={progress} />
  }

  if (!latestAnalysis) {
    return <EmptyState onRunAnalysis={handleRunAnalysis} />
  }

  const currentStageInfo = STAGES.find((s) => s.key === latestAnalysis.currentStage)

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-warm-200 bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-warm-900">
              Validation analysis
            </h1>
            <p className="text-sm text-warm-600 mt-1">
              {latestAnalysis.evidenceCount} evidence pieces · {latestAnalysis.validatedStages} of 5 stages validated
            </p>
          </div>
          <div className="flex items-center gap-3">
            {latestAnalysis.artifactId && (
              <button
                onClick={() => onViewReport?.(latestAnalysis.artifactId!)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm border border-warm-300 rounded-lg hover:bg-warm-50 transition-colors"
              >
                <FileText className="w-4 h-4" />
                View report
              </button>
            )}
            <button
              onClick={handleRunAnalysis}
              disabled={startAnalysis.isPending}
              className="flex items-center gap-2 px-5 py-2.5 bg-business text-white text-sm font-medium rounded-lg hover:bg-business-dark transition-colors disabled:opacity-50 shadow-sm"
            >
              <RefreshCw className={`w-4 h-4 ${startAnalysis.isPending ? 'animate-spin' : ''}`} />
              Re-analyze
            </button>
          </div>
        </div>
      </div>

      {/* Main Content - 3 Column Layout */}
      <div className="flex-1 min-h-0 flex">
        {/* Left: Stage Navigation */}
        <div className="w-72 flex-shrink-0 border-r border-warm-200 bg-warm-50/50 p-4 overflow-y-auto">
          <h3 className="text-xs font-semibold text-warm-500 tracking-wide mb-3 px-1">
            Validation stages
          </h3>
          <div className="space-y-2">
            {STAGES.map((stage, index) => (
              <StageNavItem
                key={stage.key}
                stage={stage}
                stageNumber={index + 1}
                score={latestAnalysis.stageScores?.find((s) => s.stageKey === stage.key)}
                isSelected={selectedStage === stage.key}
                onClick={() => setSelectedStage(stage.key)}
              />
            ))}
          </div>

          {/* Current Stage Indicator */}
          <div className="mt-6 p-4 bg-business/5 rounded-xl border border-business/20">
            <div className="text-xs text-business font-semibold tracking-wide mb-1">
              Current focus
            </div>
            <div className="font-display text-sm font-bold text-warm-900">
              {currentStageInfo?.name ?? 'Unknown'}
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-4 text-xs text-warm-500 px-1">
            Last analyzed{' '}
            {latestAnalysis.completedAt
              ? new Date(latestAnalysis.completedAt).toLocaleString()
              : 'not yet'}
          </div>
        </div>

        {/* Center: Spider Diagram */}
        <div className="flex-1 min-w-0 flex flex-col items-center justify-center p-6 bg-gradient-to-br from-warm-50/50 to-white">
          <SpiderDiagram
            scores={scores}
            selectedStage={selectedStage}
            onSelectStage={setSelectedStage}
          />
        </div>

        {/* Right: Stage Details */}
        <div className="w-[420px] flex-shrink-0 border-l border-warm-200 bg-warm-50/30 p-4 overflow-y-auto">
          <StageDetailPanel stageKey={selectedStage} stageScore={selectedStageScore} />
        </div>
      </div>
    </div>
  )
}

export default ValidationDashboard
